import { supabase } from '@/integrations/supabase/client'
import { getCachedSession, setCachedSession, clearSessionCache } from './auth-cache'

// Listen for auth state changes to clear cache
supabase.auth.onAuthStateChange(() => {
  clearSessionCache();
});

// Base URL for edge functions
const EDGE_BASE_URL = import.meta.env.VITE_SUPABASE_EDGE_URL ?? '/functions/v1'

// Standardized request helper with authentication
export async function request<T>(
  path: string, 
  opts: RequestInit = {}
): Promise<T> {
  // Try to get cached session first
  let session = getCachedSession();

  // Refresh from Supabase if we have no session OR the cached token is missing /
  // (near-)expired. Previously we only refetched when the session was entirely
  // absent, so an expired cached token would be sent as-is — or we'd fall back to
  // the anon key — and authenticated edge functions (e.g. broadcasts) rejected it
  // with 401 Unauthorized. supabase.auth.getSession() returns the auto-refreshed
  // token; if it's still expired we force a refresh.
  const nowSec = Math.floor(Date.now() / 1000);
  const tokenExpired = !session?.access_token
    || (typeof session.expires_at === 'number' && session.expires_at <= nowSec + 30);

  if (tokenExpired) {
    const { data: { session: freshSession } } = await supabase.auth.getSession();
    session = freshSession;
    if (
      session?.expires_at
      && session.expires_at <= nowSec
    ) {
      // Still stale — force a token refresh.
      const { data: refreshed } = await supabase.auth.refreshSession();
      if (refreshed?.session) session = refreshed.session;
    }
    if (session) {
      setCachedSession(session);
    }
  }
  
  // Build headers with authentication
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    ...(opts.headers ?? {})
  }

  // Add authorization header - use session token if available, otherwise use anon key
  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`
  } else {
    // For public endpoints, send anon key as bearer token
    headers['Authorization'] = `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`
  }
  
  // Make the request
  const fullUrl = `${EDGE_BASE_URL}${path}`;
  const res = await fetch(fullUrl, {
    headers,
    ...opts,
  })

  // Handle errors
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))


    const error = new Error(body.error ?? res.statusText)
    // Add status to the error object for better error handling
    ;(error as any).status = res.status
    throw error
  }

  // Parse JSON response
  const data = await res.json();

  return data as T
}

// Helper for invoking edge functions (Supabase style)
export async function invokeEdgeFunction<T>(
  functionName: string,
  options: {
    body?: any
    headers?: { [key: string]: string }
    method?: string
  } = {}
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(functionName, options)
  
  if (error) throw error
  return data as T
}